import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PullToRefresh } from '@/components/PullToRefresh';
import { CalendarDays, Plus, Clock, Trash2, ChevronLeft, ChevronRight, Upload, Download, FileSpreadsheet, Check, ArrowRightIcon, Loader2, Link2, Unlink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting', color: '#0ea5e9' },
  { value: 'roster', label: 'Roster', color: '#10b981' },
  { value: 'training', label: 'Training', color: '#f59e0b' },
  { value: 'flight', label: 'Flight Ops', color: '#8b5cf6' },
  { value: 'review', label: 'Review', color: '#ef4444' },
  { value: 'other', label: 'Other', color: '#6b7280' },
];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importStep, setImportStep] = useState('upload'); // upload | mapping | result
  const [csvData, setCsvData] = useState(null); // { headers, preview, auto_map, total_rows, allRows }
  const [columnMap, setColumnMap] = useState({ name: '', date: '', shift: '', start_time: '', end_time: '', aircraft: '', duty_type: '' });
  const [form, setForm] = useState({
    title: '', description: '', event_type: 'meeting',
    start_time: '', end_time: '', all_day: false, color: '#0ea5e9'
  });

  const fetchEvents = useCallback(async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await fetch(`${API}/calendar/events?month=${month}&year=${year}`, { credentials: 'include' });
      if (res.ok) setEvents(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  const handleRefresh = useCallback(async () => {
    await fetchEvents();
    toast.success('Calendar refreshed');
  }, [fetchEvents]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API}/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Event created');
        setCreateOpen(false);
        resetForm();
        fetchEvents();
      }
    } catch { toast.error('Failed to create event'); }
  };

  const handleDelete = async (eventId) => {
    try {
      await fetch(`${API}/calendar/events/${eventId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', event_type: 'meeting', start_time: '', end_time: '', all_day: false, color: '#0ea5e9' });
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      // Step 1: Parse CSV to get headers and preview
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/calendar/parse-csv`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setCsvData(data);
        setColumnMap(data.auto_map || { name: '', date: '', shift: '', start_time: '', end_time: '', aircraft: '', duty_type: '' });
        setImportStep('mapping');
        // Also read all rows client-side for the import step
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const allRows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row = {};
          headers.forEach((h, i) => { row[h] = vals[i] || ''; });
          return row;
        });
        setCsvData(prev => ({ ...prev, allRows }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to parse CSV');
      }
    } catch {
      toast.error('Failed to parse CSV');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!csvData?.allRows) return;
    setImporting(true);
    try {
      const res = await fetch(`${API}/calendar/import-roster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rows: csvData.allRows, mapping: columnMap })
      });
      if (res.ok) {
        const data = await res.json();
        setImportResult(data);
        setImportStep('result');
        if (data.imported > 0) {
          toast.success(`Imported ${data.imported} roster entries`);
          fetchEvents();
        }
        if (data.total_errors > 0) {
          toast.warning(`${data.total_errors} rows had errors`);
        }
      } else {
        toast.error('Import failed');
      }
    } catch {
      toast.error('Import request failed');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep('upload');
    setCsvData(null);
    setImportResult(null);
    setColumnMap({ name: '', date: '', shift: '', start_time: '', end_time: '', aircraft: '', duty_type: '' });
  };

  // Air Maestro state
  const [amTab, setAmTab] = useState('csv'); // csv | airmaestro
  const [amConnecting, setAmConnecting] = useState(false);
  const [amConnected, setAmConnected] = useState(null);
  const [amFetching, setAmFetching] = useState(false);
  const [amError, setAmError] = useState('');
  const [amCreds, setAmCreds] = useState({ site_url: '', username: '', password: '' });
  const [amDateRange, setAmDateRange] = useState({ from_date: '', to_date: '' });

  const handleAmConnect = async () => {
    setAmConnecting(true);
    setAmError('');
    try {
      const res = await fetch(`${API}/calendar/airmaestro/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(amCreds)
      });
      const data = await res.json();
      if (res.ok && data.connected) {
        setAmConnected(data);
        toast.success('Connected to Air Maestro');
      } else {
        setAmError(data.detail || 'Connection failed');
        toast.error(data.detail || 'Connection failed');
      }
    } catch {
      setAmError('Failed to connect');
    } finally {
      setAmConnecting(false);
    }
  };

  const handleAmFetchRoster = async () => {
    setAmFetching(true);
    setAmError('');
    try {
      const res = await fetch(`${API}/calendar/airmaestro/fetch-roster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(amDateRange)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCsvData(data);
        setColumnMap(data.auto_map || { name: '', date: '', shift: '', start_time: '', end_time: '', aircraft: '', duty_type: '' });
        setImportStep('mapping');
        toast.success(`Found ${data.total_rows} roster entries`);
      } else {
        setAmError(data.message || data.detail || 'Could not fetch roster data');
      }
    } catch {
      setAmError('Fetch failed');
    } finally {
      setAmFetching(false);
    }
  };

  const handleAmDisconnect = async () => {
    await fetch(`${API}/calendar/airmaestro/disconnect`, { method: 'POST', credentials: 'include' });
    setAmConnected(null);
    setAmCreds({ site_url: '', username: '', password: '' });
    toast.success('Disconnected');
  };

  const downloadTemplate = () => {
    const csv = 'Name,Date,Shift,Start Time,End Time\nJohn Smith,2026-03-01,Day,06:00,14:00\nJane Doe,2026-03-01,Night,14:00,22:00\nBob Wilson,2026-03-02,Day,06:00,14:00\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roster_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openCreate = (date) => {
    const d = date || new Date();
    const dateStr = format(d, 'yyyy-MM-dd');
    setForm({
      ...form,
      start_time: `${dateStr}T09:00:00`,
      end_time: `${dateStr}T10:00:00`,
    });
    setCreateOpen(true);
  };

  // Calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start of month
  const startDay = monthStart.getDay();
  const paddedDays = [...Array(startDay).fill(null), ...daysInMonth];

  const getEventsForDay = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(e => e.start_time?.startsWith(dateStr));
  };

  const dayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="calendar-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Calendar
            </h1>
            <p className="text-slate-500 text-sm mt-1">Roster management and meeting coordination</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              data-testid="import-roster-btn"
              onClick={() => setImportOpen(true)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white flex-1 sm:flex-none text-xs sm:text-sm"
            >
              <Upload className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Import Roster</span>
            </Button>
            <Button
              data-testid="create-event-btn"
              onClick={() => openCreate(new Date())}
              className="bg-[#003087] hover:bg-[#003087]/90 text-white flex-1 sm:flex-none text-xs sm:text-sm"
            >
              <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">New Event</span><span className="sm:hidden">Event</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 sm:gap-6">
          {/* Calendar Grid */}
          <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="border-b border-slate-800 p-3 sm:p-4 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="text-slate-400 hover:text-white h-8 w-8 p-0">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-base sm:text-lg font-semibold text-slate-200" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {format(currentMonth, 'MMM yyyy')}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="text-slate-400 hover:text-white h-8 w-8 p-0">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date())} className="text-xs text-slate-400 hover:text-white">
              Today
            </Button>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} className="text-center text-[9px] sm:text-[10px] font-mono text-slate-500 uppercase tracking-wider py-1 sm:py-2">
                  <span className="sm:hidden">{d}</span>
                  <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</span>
                </div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {paddedDays.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} className="h-14 sm:h-24 bg-slate-950/30 rounded" />;
                const dayEvts = getEventsForDay(day);
                const isToday = isSameDay(day, new Date());
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                return (
                  <button
                    key={idx}
                    data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
                    onClick={() => setSelectedDate(day)}
                    className={`h-14 sm:h-24 rounded p-1 sm:p-1.5 text-left transition-colors relative ${
                      isSelected ? 'bg-[#003087]/20 border border-[#003087]' :
                      isToday ? 'bg-slate-800/60 border border-slate-700' :
                      'bg-slate-950/30 hover:bg-slate-800/40 border border-transparent'
                    }`}
                  >
                    <span className={`text-[10px] sm:text-xs font-medium block ${isToday ? 'text-[#0ea5e9]' : 'text-slate-400'}`}>
                      {format(day, 'd')}
                    </span>
                    {/* Mobile: Show dots */}
                    <div className="sm:hidden mt-1 flex gap-0.5 flex-wrap">
                      {dayEvts.slice(0, 3).map(evt => (
                        <div
                          key={evt.event_id}
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: evt.color }}
                        />
                      ))}
                    </div>
                    {/* Desktop: Show event names */}
                    <div className="hidden sm:block mt-1 space-y-0.5 overflow-hidden">
                      {dayEvts.slice(0, 3).map(evt => (
                        <div
                          key={evt.event_id}
                          className="text-[9px] truncate rounded px-1 py-0.5"
                          style={{ backgroundColor: `${evt.color}20`, color: evt.color }}
                        >
                          {evt.title}
                        </div>
                      ))}
                      {dayEvts.length > 3 && (
                        <span className="text-[9px] text-slate-500">+{dayEvts.length - 3} more</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Day Detail */}
        <div className="space-y-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800 p-4">
              <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#0ea5e9]" />
                {selectedDate ? format(selectedDate, 'EEEE, MMM d') : 'Select a date'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {selectedDate ? (
                <>
                  {dayEvents.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-xs text-slate-500">No events this day</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openCreate(selectedDate)}
                        className="text-[#0ea5e9] hover:text-[#0ea5e9] mt-2 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add event
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dayEvents.map(evt => {
                        const evtType = EVENT_TYPES.find(t => t.value === evt.event_type);
                        return (
                          <div
                            key={evt.event_id}
                            data-testid={`event-${evt.event_id}`}
                            className="bg-slate-800/50 border border-slate-700/50 rounded-md p-3"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: evt.color }} />
                                <h4 className="text-sm text-slate-200 font-medium">{evt.title}</h4>
                              </div>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleDelete(evt.event_id)}
                                className="h-6 w-6 p-0 text-slate-500 hover:text-red-400"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            {evt.description && (
                              <p className="text-xs text-slate-500 mt-1">{evt.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500 font-mono">
                              <Clock className="h-3 w-3" />
                              {evt.start_time ? format(parseISO(evt.start_time), 'HH:mm') : ''}
                              {evt.end_time ? ` - ${format(parseISO(evt.end_time), 'HH:mm')}` : ''}
                            </div>
                            <Badge className="mt-2 text-[10px]" style={{ backgroundColor: `${evt.color}15`, color: evt.color, borderColor: `${evt.color}30` }}>
                              {evtType?.label || evt.event_type}
                            </Badge>
                          </div>
                        );
                      })}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openCreate(selectedDate)}
                        className="text-[#0ea5e9] hover:text-[#0ea5e9] text-xs w-full"
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add event
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-500 text-center py-6">Click a date to view events</p>
              )}
            </CardContent>
          </Card>

          {/* Event Types Legend */}
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-3">Event Types</p>
              <div className="space-y-2">
                {EVENT_TYPES.map(t => (
                  <div key={t.value} className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="text-xs text-slate-400">{t.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0f172a] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-200">New Event</DialogTitle>
            <DialogDescription className="text-slate-500">Schedule a roster entry, meeting, or event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              data-testid="event-title-input"
              placeholder="Event title"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              className="bg-slate-900 border-slate-800 text-slate-200"
            />
            <Textarea
              data-testid="event-desc-input"
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="bg-slate-900 border-slate-800 text-slate-200"
            />
            <Select
              value={form.event_type}
              onValueChange={(v) => {
                const evtType = EVENT_TYPES.find(t => t.value === v);
                setForm({...form, event_type: v, color: evtType?.color || '#0ea5e9'});
              }}
            >
              <SelectTrigger data-testid="event-type-select" className="bg-slate-900 border-slate-800 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700">
                {EVENT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value} className="text-slate-300 focus:bg-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1.5 block">Start</label>
                <Input
                  data-testid="event-start-input"
                  type="datetime-local"
                  value={form.start_time}
                  onChange={(e) => setForm({...form, start_time: e.target.value})}
                  className="bg-slate-900 border-slate-800 text-slate-200 text-sm [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1.5 block">End</label>
                <Input
                  data-testid="event-end-input"
                  type="datetime-local"
                  value={form.end_time}
                  onChange={(e) => setForm({...form, end_time: e.target.value})}
                  className="bg-slate-900 border-slate-800 text-slate-200 text-sm [color-scheme:dark]"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Cancel</Button>
            <Button
              data-testid="event-save-btn"
              onClick={handleCreate}
              className="bg-[#003087] hover:bg-[#003087]/90 text-white"
            >
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Roster Dialog */}
      <Dialog open={importOpen} onOpenChange={() => { setImportOpen(false); resetImport(); }}>
        <DialogContent className="max-w-2xl bg-[#0f172a] border-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-slate-200 flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
              Import Roster
              {csvData?.total_rows > 0 && importStep !== 'upload' && (
                <span className="text-xs text-slate-500 font-normal ml-2">({csvData.total_rows} rows)</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {importStep === 'upload' && 'Upload a CSV file from Air Maestro or any rostering system'}
              {importStep === 'mapping' && 'Map your CSV columns to roster fields'}
              {importStep === 'result' && 'Import complete'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center gap-2 mt-2 mb-4">
            {['Upload', 'Map Columns', 'Done'].map((label, i) => {
              const stepIdx = i;
              const currentIdx = importStep === 'upload' ? 0 : importStep === 'mapping' ? 1 : 2;
              return (
                <React.Fragment key={label}>
                  {i > 0 && <div className={`flex-1 h-px ${stepIdx <= currentIdx ? 'bg-emerald-500' : 'bg-slate-700'}`} />}
                  <div className={`flex items-center gap-1.5 text-xs font-mono ${stepIdx <= currentIdx ? 'text-emerald-400' : 'text-slate-600'}`}>
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${
                      stepIdx < currentIdx ? 'bg-emerald-500 text-white' : stepIdx === currentIdx ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500' : 'bg-slate-800 text-slate-600 border border-slate-700'
                    }`}>
                      {stepIdx < currentIdx ? <Check className="h-3 w-3" /> : i + 1}
                    </div>
                    <span className="hidden sm:inline">{label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* STEP 1: Upload */}
            {importStep === 'upload' && (
              <>
                {/* Tab selector: CSV vs Air Maestro */}
                <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-md p-1">
                  <button
                    data-testid="tab-csv-import"
                    onClick={() => setAmTab('csv')}
                    className={`flex-1 text-xs font-mono py-2 px-3 rounded transition-colors ${
                      amTab === 'csv' ? 'bg-[#003087] text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    CSV File
                  </button>
                  <button
                    data-testid="tab-airmaestro-import"
                    onClick={() => setAmTab('airmaestro')}
                    className={`flex-1 text-xs font-mono py-2 px-3 rounded transition-colors flex items-center justify-center gap-2 ${
                      amTab === 'airmaestro' ? 'bg-[#003087] text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <Link2 className="h-3 w-3" /> Air Maestro
                  </button>
                </div>

                {amTab === 'csv' && (
                  <>
                    <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
                      <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-2">Supported Sources</p>
                      <div className="flex flex-wrap gap-2">
                        {['Air Maestro CSV', 'Generic CSV', 'Excel Export'].map(s => (
                          <span key={s} className="text-[11px] bg-slate-800 text-slate-400 px-2 py-1 rounded">{s}</span>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-3">
                        Columns are auto-detected. You can adjust the mapping in the next step.
                      </p>
                    </div>

                    <Button
                      data-testid="download-template-btn"
                      variant="outline"
                      onClick={downloadTemplate}
                      className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <Download className="h-4 w-4 mr-2" /> Download Template CSV
                    </Button>

                    <div className="relative">
                      <input
                        data-testid="roster-csv-input"
                        type="file"
                        accept=".csv"
                        onChange={handleImportCSV}
                        disabled={importing}
                        className="block w-full text-sm text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#003087] file:text-white hover:file:bg-[#003087]/90 file:cursor-pointer cursor-pointer bg-slate-900 border border-slate-800 rounded-md"
                      />
                    </div>

                    {importing && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                        Parsing CSV...
                      </div>
                    )}
                  </>
                )}

                {amTab === 'airmaestro' && (
                  <>
                    {amConnected ? (
                      <div className="space-y-4">
                        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-md p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <Check className="h-4 w-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-sm text-emerald-400 font-medium">Connected</p>
                              <p className="text-[11px] text-slate-500 font-mono">{amConnected.site_url}</p>
                            </div>
                          </div>
                          <Button
                            data-testid="am-disconnect-btn"
                            variant="ghost"
                            size="sm"
                            onClick={handleAmDisconnect}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Unlink className="h-4 w-4 mr-1" /> Disconnect
                          </Button>
                        </div>

                        <div>
                          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-2">Date Range (optional)</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] text-slate-500 mb-1 block">From</label>
                              <Input
                                data-testid="am-from-date"
                                type="date"
                                value={amDateRange.from_date}
                                onChange={(e) => setAmDateRange(prev => ({ ...prev, from_date: e.target.value }))}
                                className="bg-slate-900 border-slate-800 text-slate-200 text-sm [color-scheme:dark]"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-500 mb-1 block">To</label>
                              <Input
                                data-testid="am-to-date"
                                type="date"
                                value={amDateRange.to_date}
                                onChange={(e) => setAmDateRange(prev => ({ ...prev, to_date: e.target.value }))}
                                className="bg-slate-900 border-slate-800 text-slate-200 text-sm [color-scheme:dark]"
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          data-testid="am-fetch-roster-btn"
                          onClick={handleAmFetchRoster}
                          disabled={amFetching}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {amFetching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                          Fetch Roster
                        </Button>

                        {amError && (
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-3 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-amber-400">{amError}</p>
                              <p className="text-[10px] text-slate-500 mt-1">Tip: Export the roster as CSV from Air Maestro (Timesheets &gt; Tools &gt; Transfer &gt; Export) and use the CSV import tab instead.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
                          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-2">Connect to Air Maestro</p>
                          <p className="text-[11px] text-slate-500">
                            Enter your Air Maestro site URL and credentials to fetch roster data directly.
                          </p>
                        </div>

                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1.5 block">Site URL</label>
                          <Input
                            data-testid="am-site-url"
                            placeholder="yoursite.airmaestro.net"
                            value={amCreds.site_url}
                            onChange={(e) => setAmCreds(prev => ({ ...prev, site_url: e.target.value }))}
                            className="bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1.5 block">Username</label>
                          <Input
                            data-testid="am-username"
                            placeholder="Your Air Maestro username"
                            value={amCreds.username}
                            onChange={(e) => setAmCreds(prev => ({ ...prev, username: e.target.value }))}
                            className="bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1.5 block">Password</label>
                          <Input
                            data-testid="am-password"
                            type="password"
                            placeholder="Your Air Maestro password"
                            value={amCreds.password}
                            onChange={(e) => setAmCreds(prev => ({ ...prev, password: e.target.value }))}
                            className="bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                          />
                        </div>

                        <Button
                          data-testid="am-connect-btn"
                          onClick={handleAmConnect}
                          disabled={amConnecting || !amCreds.site_url || !amCreds.username || !amCreds.password}
                          className="w-full bg-[#003087] hover:bg-[#003087]/90 text-white"
                        >
                          {amConnecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
                          Connect
                        </Button>

                        {amError && (
                          <div className="bg-red-500/10 border border-red-500/20 rounded-md p-3 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-400">{amError}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* STEP 2: Column Mapping */}
            {importStep === 'mapping' && csvData && (
              <>
                <div className="space-y-3">
                  <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">Column Mapping</p>
                  {[
                    { key: 'name', label: 'Name / Crew Member', required: false },
                    { key: 'date', label: 'Date', required: true },
                    { key: 'shift', label: 'Shift / Shift Code', required: false },
                    { key: 'start_time', label: 'Start Time', required: false },
                    { key: 'end_time', label: 'End Time', required: false },
                    { key: 'aircraft', label: 'Aircraft / Registration', required: false },
                    { key: 'duty_type', label: 'Duty Type / Activity', required: false },
                  ].map(field => (
                    <div key={field.key} className="flex items-center gap-3">
                      <div className="w-44 flex-shrink-0">
                        <span className="text-xs text-slate-300">{field.label}</span>
                        {field.required && <span className="text-red-400 ml-1 text-[10px]">*</span>}
                      </div>
                      <Select
                        value={columnMap[field.key] || '_none_'}
                        onValueChange={(v) => setColumnMap(prev => ({ ...prev, [field.key]: v === '_none_' ? '' : v }))}
                      >
                        <SelectTrigger data-testid={`map-${field.key}`} className="bg-slate-900 border-slate-800 text-slate-200 text-xs h-8">
                          <SelectValue placeholder="-- Skip --" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-slate-700">
                          <SelectItem value="_none_" className="text-slate-500 text-xs focus:bg-slate-800">-- Skip --</SelectItem>
                          {csvData.headers.map(h => (
                            <SelectItem key={h} value={h} className="text-slate-300 text-xs focus:bg-slate-800">{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                {/* Preview Table */}
                <div>
                  <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-2">Preview (first 5 rows)</p>
                  <div className="bg-slate-950 rounded-md border border-slate-800 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-800">
                          {csvData.headers.map(h => (
                            <th key={h} className="px-3 py-2 text-left font-mono text-[10px] text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.preview.map((row, i) => (
                          <tr key={i} className="border-b border-slate-800/50">
                            {csvData.headers.map(h => (
                              <td key={h} className="px-3 py-1.5 text-slate-400 whitespace-nowrap">{row[h]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* STEP 3: Result */}
            {importStep === 'result' && importResult && (
              <div data-testid="import-result" className="space-y-4">
                <div className="bg-slate-900/50 border border-slate-800 rounded-md p-6 text-center animate-fade-in">
                  <div className="flex items-center justify-center gap-6 text-lg">
                    <div className="text-emerald-400">
                      <span className="font-bold text-3xl">{importResult.imported}</span>
                      <p className="text-xs mt-1">Imported</p>
                    </div>
                    {importResult.total_errors > 0 && (
                      <div className="text-amber-400">
                        <span className="font-bold text-3xl">{importResult.total_errors}</span>
                        <p className="text-xs mt-1">Errors</p>
                      </div>
                    )}
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="bg-slate-900/50 border border-slate-800 rounded-md p-3">
                    <p className="font-mono text-[10px] text-amber-400 uppercase tracking-wider mb-2">Errors</p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-[11px] text-amber-400/70 font-mono">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            {importStep === 'mapping' && (
              <>
                <Button variant="ghost" onClick={() => { setImportStep('upload'); setCsvData(null); }} className="text-slate-400">Back</Button>
                <Button
                  data-testid="confirm-import-btn"
                  onClick={handleConfirmImport}
                  disabled={importing || !columnMap.date}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Import {csvData?.total_rows || 0} Rows
                </Button>
              </>
            )}
            {importStep === 'result' && (
              <>
                <Button variant="ghost" onClick={() => { resetImport(); }} className="text-slate-400">Import Another</Button>
                <Button onClick={() => { setImportOpen(false); resetImport(); }} className="bg-[#003087] hover:bg-[#003087]/90 text-white">Done</Button>
              </>
            )}
            {importStep === 'upload' && (
              <Button variant="ghost" onClick={() => { setImportOpen(false); resetImport(); }} className="text-slate-400">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </PullToRefresh>
  );
}
